import { useCallback, useEffect, useMemo, useState } from "react";

import { usersService } from "@/modules/dashboard/users/services/users.service";
import type { UserStatus } from "@/modules/dashboard/users/types/create-user.types";
import type { UserListItem, UserStatusCounts } from "@/modules/dashboard/users/types/users-api.types";
import { toastApiError } from "@/shared/utils/toast";

import {
  ACCESS_SEARCH_DEBOUNCE_MS,
  ACCESS_SEARCH_MIN_LENGTH,
  ACCESS_USERS_PAGE_SIZE,
} from "../constants/accessCatalog.constants";
import { principalRolesService } from "../services/principalRolesService";
import type { AssignedRole } from "../types/access.types";
import { usePrincipalRoles, type PrincipalRolesConfig, type RolesByPrincipal } from "./usePrincipalRoles";

/** Map of user id → its currently assigned roles. */
export type RolesByUser = RolesByPrincipal;

/** The `"all"` sentinel means "no filter", and must never reach the wire — it would 400. */
export const ACCESS_STATUS_ALL = "all" as const;
export const ACCESS_ROLE_ALL = "all" as const;

export type AccessStatusFilter = typeof ACCESS_STATUS_ALL | UserStatus;

// Module-scoped so the reference is stable — usePrincipalRoles keys its callbacks on this object (see its
// PrincipalRolesConfig doc for why an inline object would loop the provider's mount effect).
const USERS_CONFIG: PrincipalRolesConfig<UserListItem> = {
  /**
   * One request for the whole page, roles included. Every optional filter is spread conditionally rather than
   * assigned as `undefined`/`""`: `buildQueryString` keeps empty strings and the API runs
   * `forbidNonWhitelisted`, so a stray `?status=` is a 400, not an ignored param.
   */
  fetchPage: async ({ skip, limit, search, status, roleId }) => {
    const response = await usersService.fetchList({
      skip,
      limit,
      include: "roles",
      ...(search ? { search } : {}),
      ...(status ? { status: status as UserStatus } : {}),
      ...(roleId ? { role_id: roleId } : {}),
    });
    // `roles` is always present because we asked for it; `[]` is authoritative (the user holds none), so
    // the per-principal fan-out is skipped entirely and no id can land in `rolesUnavailable`.
    const rolesById: RolesByPrincipal = {};
    for (const user of response.data) {
      rolesById[user.id] = (user.roles ?? []) as AssignedRole[];
    }
    return { data: response.data, total: response.total, rolesById };
  },
  listRoles: (userId) => principalRolesService.listUserRoles(userId),
  assignRoles: (userId, roleIds) => principalRolesService.assignUserRoles(userId, roleIds),
  removeRole: (userId, roleId) => principalRolesService.removeUserRole(userId, roleId),
  initialLimit: ACCESS_USERS_PAGE_SIZE,
};

export interface AccessSearchState {
  /** Something was typed, but not enough characters to search yet — show the hint, send nothing. */
  isTermTooShort: boolean;
  /** The term to put on the wire, or `undefined` when it must not be sent. */
  serverSearch: string | undefined;
}

/**
 * The two signals come from different values on purpose: the hint reacts to what is being typed right now,
 * while the request waits for the debounced value. Deriving both from one would either lag the hint or fire a
 * request per keystroke.
 */
export function resolveAccessSearch(query: string, debouncedQuery: string): AccessSearchState {
  const trimmed = query.trim();
  return {
    isTermTooShort: trimmed.length > 0 && trimmed.length < ACCESS_SEARCH_MIN_LENGTH,
    serverSearch: debouncedQuery.length >= ACCESS_SEARCH_MIN_LENGTH ? debouncedQuery : undefined,
  };
}

/**
 * The users half of Access & permissions: one page of `GET /internal/users` with its roles embedded
 * (`?include=roles`), the server-side filters, and the status counts that feed the header stats.
 *
 * Filters and pagination live HERE, not in `usePrincipalRoles`, so the shared state machine never acquires
 * concepts the API Keys section has no endpoint for.
 */
export function useAccessUsers() {
  const principal = usePrincipalRoles(USERS_CONFIG);
  const { loadWith } = principal;

  const [page, setPage] = useState(1);
  const [query, setQueryState] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [status, setStatusState] = useState<AccessStatusFilter>(ACCESS_STATUS_ALL);
  const [roleId, setRoleIdState] = useState<string>(ACCESS_ROLE_ALL);
  // `null` is UNKNOWN, never zero: a failed counts request must not read as "nobody is missing a role".
  const [counts, setCounts] = useState<UserStatusCounts | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  /** Bumped after a mutation so the derived counts are refetched — see the counts effect. */
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), ACCESS_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const { isTermTooShort, serverSearch } = resolveAccessSearch(query, debouncedQuery);

  // Page resets happen in the SETTER, never in an effect. An effect would first fire a request with the old
  // `skip` — e.g. skip=200 against a 3-row result — and paint an empty table under a total of 3.
  const setSearch = useCallback((next: string) => {
    setQueryState(next);
    setPage(1);
  }, []);
  const setStatus = useCallback((next: AccessStatusFilter) => {
    setStatusState(next);
    setPage(1);
  }, []);
  const setRoleId = useCallback((next: string) => {
    setRoleIdState(next);
    setPage(1);
  }, []);

  const wireStatus = status === ACCESS_STATUS_ALL ? undefined : status;
  const wireRoleId = roleId === ACCESS_ROLE_ALL ? undefined : roleId;

  // One effect drives every fetch: each filter is both a query param and a dependency, so changing any of
  // them re-issues from the right offset without a separate "reset pagination" step. `loadWith` is stable.
  //
  // `runFetch` rethrows anything that is not a 401/403, so the rejection has to be handled here — the
  // provider used to do it when it owned this load. And it is recorded, not just toasted: a failed load
  // leaves the table empty, and an empty table with no error reads as "no user matches", which is a
  // different and much more reassuring statement than "we could not load this".
  // `cancelled` is not optional. `runFetch` resolves normally when it discards a stale response (it
  // `return`s inside the try), so every superseded call still settles its own promise chain: without this
  // guard a late success would clear the error flag of a NEWER request that actually failed, and a late
  // rejection would raise it over one that succeeded — plus one toast per superseded request on a flaky
  // network. `reloadNonce` is deliberately NOT a dependency: a role toggle does not change which users
  // match the filter, and refetching here would replace `users`, which closes the open drawer.
  useEffect(() => {
    let cancelled = false;
    void loadWith({
      skip: (page - 1) * ACCESS_USERS_PAGE_SIZE,
      limit: ACCESS_USERS_PAGE_SIZE,
      ...(serverSearch ? { search: serverSearch } : {}),
      ...(wireStatus ? { status: wireStatus } : {}),
      ...(wireRoleId ? { roleId: wireRoleId } : {}),
    })
      .then(() => {
        if (!cancelled) setLoadFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setLoadFailed(true);
        toastApiError(error);
      });
    return () => {
      cancelled = true;
    };
  }, [loadWith, page, serverSearch, wireStatus, wireRoleId]);

  // Counts take the same filters MINUS `status` (each chip reports its own bucket) and minus pagination.
  //
  // `reloadNonce` is in the deps because assigning a role changes `no_roles` — without it the header would
  // keep claiming "1 without roles" right after the operator gave that person a role, with the arithmetic
  // pointing the opposite way to what they just did. It is one cheap grouped query.
  useEffect(() => {
    let cancelled = false;
    usersService
      .fetchCounts({
        ...(serverSearch ? { search: serverSearch } : {}),
        ...(wireRoleId ? { role_id: wireRoleId } : {}),
      })
      .then((data) => {
        if (!cancelled) setCounts(data);
      })
      .catch(() => {
        if (!cancelled) setCounts(null);
      });
    return () => {
      cancelled = true;
    };
  }, [serverSearch, wireRoleId, reloadNonce]);

  // Mutations keep the shared hook's behaviour and additionally invalidate the derived counts.
  const { assignRoles: assignRolesBase, removeRole: removeRoleBase } = principal;
  const assignRoles = useCallback(
    async (userId: string, roleIds: string[]) => {
      const result = await assignRolesBase(userId, roleIds);
      setReloadNonce((n) => n + 1);
      return result;
    },
    [assignRolesBase],
  );
  const removeRole = useCallback(
    async (userId: string, roleId: string) => {
      await removeRoleBase(userId, roleId);
      setReloadNonce((n) => n + 1);
    },
    [removeRoleBase],
  );

  return useMemo(
    () => ({
      users: principal.items,
      total: principal.total,
      rolesByUser: principal.rolesById,
      rolesUnavailable: principal.rolesUnavailable,
      isLoading: principal.isLoading,
      hasNoAccess: principal.hasNoAccess,
      load: principal.load,
      assignRoles,
      removeRole,
      loadFailed,
      // Filters + pagination
      page,
      pageSize: ACCESS_USERS_PAGE_SIZE,
      setPage,
      query,
      setSearch,
      isTermTooShort,
      status,
      setStatus,
      roleId,
      setRoleId,
      counts,
    }),
    [
      principal,
      assignRoles,
      removeRole,
      loadFailed,
      page,
      query,
      setSearch,
      isTermTooShort,
      status,
      setStatus,
      roleId,
      setRoleId,
      counts,
    ],
  );
}
